import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PeriodSelector } from '../components/PeriodSelector';
import { startOfMonth, endOfMonth, format as fmtDate } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { 
  Upload, 
  Users, 
  Clock,
  TrendingUp,
  ChevronLeft,
  UserCircle,
  Receipt,
  Calendar,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const FrontOfHousePage = () => {
  const { currentLocation, api } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedDate = searchParams.get('date');
  
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(new Date()));
  const [periodPreset, setPeriodPreset] = useState('this_month');

  // Order detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const openOrderDetail = async (orderId) => {
    if (!orderId) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load order');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (currentLocation) {
      if (selectedDate) {
        fetchDailyData(selectedDate);
      } else {
        fetchSummary();
      }
    }
  }, [currentLocation, selectedDate, periodStart, periodEnd]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get('/orders/summary', { 
        params: {
          location_id: currentLocation.id,
          start_date: fmtDate(periodStart, 'yyyy-MM-dd'),
          end_date: fmtDate(periodEnd, 'yyyy-MM-dd'),
        } 
      });
      setSummary(res.data);
      setDailyData(null);
    } catch (err) {
      console.error('Error fetching summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyData = async (date) => {
    setLoading(true);
    try {
      const res = await api.get('/orders/daily-summary', { 
        params: { location_id: currentLocation.id, date } 
      });
      setDailyData(res.data);
      setSummary(null);
    } catch (err) {
      console.error('Error fetching daily data:', err);
      toast.error('Failed to load daily data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => `£${val?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { 
    weekday: 'long',
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Daily detail view
  if (selectedDate && dailyData) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="foh-daily-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/front-of-house')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{formatDate(selectedDate)}</h1>
              <p className="text-muted-foreground">Order details for this day</p>
            </div>
          </div>
        </div>

        {!dailyData.has_data ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No order data for this date</h3>
              <p className="text-muted-foreground mb-4">
                Import Order Details from the Imports hub.
              </p>
              <Button onClick={() => navigate('/imports?tab=orders')} data-testid="empty-open-imports-btn">
                <Upload className="w-4 h-4 mr-2" />
                Go to Imports
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{dailyData.total_orders}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Guests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{dailyData.total_guests}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-profit">{formatCurrency(dailyData.total_amount)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Avg Order</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(dailyData.avg_order_value)}</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Server Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCircle className="w-5 h-5" />
                    Server Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dailyData.server_breakdown.map((server, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <div className="font-medium">{server.server}</div>
                          <div className="text-sm text-muted-foreground">
                            {server.orders} orders • {server.guests} guests
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-profit">{formatCurrency(server.amount)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Hourly Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Hourly Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dailyData.hourly_breakdown.map((hour, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-14 text-sm font-mono text-muted-foreground">{hour.hour}</div>
                        <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
                          <div 
                            className="bg-primary h-full rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max(10, (hour.orders / Math.max(...dailyData.hourly_breakdown.map(h => h.orders))) * 100)}%` 
                            }}
                          >
                            <span className="text-xs text-primary-foreground font-medium">
                              {hour.orders}
                            </span>
                          </div>
                        </div>
                        <div className="w-20 text-right text-sm text-muted-foreground">
                          {hour.guests} guests
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Orders Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
                <CardDescription>{dailyData.orders.length} orders on this day · click a row for details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Order #</TableHead>
                        <TableHead>Server</TableHead>
                        <TableHead>Table</TableHead>
                        <TableHead className="text-center">Guests</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyData.orders.map((order) => (
                        <TableRow
                          key={order.id}
                          onClick={() => openOrderDetail(order.id)}
                          className="cursor-pointer hover:bg-muted/50"
                          data-testid={`foh-order-row-${order.id}`}
                        >
                          <TableCell className="font-mono text-sm">{formatTime(order.opened_at)}</TableCell>
                          <TableCell>#{order.order_number}</TableCell>
                          <TableCell>{order.server}</TableCell>
                          <TableCell>{order.table || '-'}</TableCell>
                          <TableCell className="text-center">{order.guests}</TableCell>
                          <TableCell className="text-right mono-num font-medium">{formatCurrency(order.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
        <OrderDetailDrawer
          open={detailOpen}
          onOpenChange={setDetailOpen}
          loading={detailLoading}
          detail={detail}
          formatCurrency={formatCurrency}
          formatTime={formatTime}
        />
      </div>
    );
  }

  // Summary view
  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="foh-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Front of House</h1>
          <p className="text-muted-foreground">Order analytics, server performance & table metrics</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <PeriodSelector
            startDate={periodStart}
            endDate={periodEnd}
            preset={periodPreset}
            onPresetChange={setPeriodPreset}
            onChange={(s, e) => { setPeriodStart(s); setPeriodEnd(e); }}
          />
          <Button
            variant="outline"
            onClick={() => navigate('/imports?tab=orders')}
            data-testid="open-imports-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import orders
          </Button>
        </div>
      </div>

      {!summary?.has_data ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No order data yet</h3>
            <p className="text-muted-foreground mb-4">
              Import an Order Details CSV from the Imports hub.
            </p>
            <Button onClick={() => navigate('/imports?tab=orders')} data-testid="empty-open-imports-btn-2">
              <Upload className="w-4 h-4 mr-2" />
              Go to Imports
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Total Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.total_orders.toLocaleString()}</div>
                <p className="text-sm text-muted-foreground">
                  {summary.avg_daily_orders} avg/day
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Total Guests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.total_guests.toLocaleString()}</div>
                <p className="text-sm text-muted-foreground">
                  Over {summary.days_count} days
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Total Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-profit">{formatCurrency(summary.total_revenue)}</div>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(summary.avg_daily_revenue)} avg/day
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Avg Order Value
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary.avg_order_value)}</div>
                <p className="text-sm text-muted-foreground">
                  Peak hour: {summary.peak_hour}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Period Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            Data from {formatDate(summary.period_start)} to {formatDate(summary.period_end)}
          </div>

          {/* Server Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCircle className="w-5 h-5" />
                Server Performance
              </CardTitle>
              <CardDescription>Revenue and tips by server</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Server</TableHead>
                      <TableHead className="text-center">Orders</TableHead>
                      <TableHead className="text-center">Guests</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Tips</TableHead>
                      <TableHead className="text-right">Avg Order</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.server_breakdown.map((server, idx) => (
                      <TableRow key={idx} className="table-row-hover">
                        <TableCell className="font-medium">{server.server}</TableCell>
                        <TableCell className="text-center">{server.orders}</TableCell>
                        <TableCell className="text-center">{server.guests}</TableCell>
                        <TableCell className="text-right mono-num font-medium text-profit">
                          {formatCurrency(server.revenue)}
                        </TableCell>
                        <TableCell className="text-right mono-num text-muted-foreground">
                          {formatCurrency(server.tips)}
                        </TableCell>
                        <TableCell className="text-right mono-num">
                          {formatCurrency(server.avg_order)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
      <OrderDetailDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        loading={detailLoading}
        detail={detail}
        formatCurrency={formatCurrency}
        formatTime={formatTime}
      />
    </div>
  );
};

const OrderDetailDrawer = ({ open, onOpenChange, loading, detail, formatCurrency, formatTime }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="order-detail-drawer">
      <SheetHeader>
        <SheetTitle>{detail ? `Order #${detail.square_order_id?.slice(-6) || detail.id?.slice(-6)}` : 'Order details'}</SheetTitle>
        <SheetDescription>
          {detail ? `${detail.provider || 'unknown'} · ${detail.closed_at ? formatTime(detail.closed_at) : ''} · ${detail.state || ''}` : 'Line items and totals from the source POS.'}
        </SheetDescription>
      </SheetHeader>
      {loading ? (
        <div className="py-10 text-center text-muted-foreground">Loading…</div>
      ) : !detail ? (
        <div className="py-10 text-center text-muted-foreground">No details available.</div>
      ) : (
        <div className="mt-6 space-y-6 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-muted-foreground">Server</div>
              <div>{detail.server || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="font-semibold">{formatCurrency(detail.total ?? detail.amount ?? 0)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Tip</div>
              <div>{formatCurrency(detail.tip ?? 0)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Tax</div>
              <div>{formatCurrency(detail.total_tax ?? 0)}</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">Line items ({detail.line_items?.length || 0})</div>
            {(!detail.line_items || detail.line_items.length === 0) ? (
              <div className="text-muted-foreground italic">No line items in source payload.</div>
            ) : (
              <div className="rounded border divide-y">
                {detail.line_items.map((li, i) => (
                  <div key={i} className="p-3" data-testid={`order-line-item-${i}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{li.name || 'Item'}{li.variation_name ? ` — ${li.variation_name}` : ''}</div>
                        {li.note && <div className="text-xs text-muted-foreground">{li.note}</div>}
                        {li.modifiers?.length > 0 && (
                          <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                            {li.modifiers.map((m, mi) => (
                              <div key={mi}>+ {m.name}{m.amount ? ` (${formatCurrency(m.amount)})` : ''}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="text-xs text-muted-foreground">×{li.quantity}</div>
                        <div className="font-medium">{formatCurrency(li.total)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {detail.tenders?.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Payment</div>
              <div className="rounded border divide-y">
                {detail.tenders.map((t, i) => (
                  <div key={i} className="p-3 flex justify-between text-sm">
                    <span className="capitalize">{(t.type || '').toLowerCase().replace('_', ' ')}</span>
                    <span>{formatCurrency(t.amount)}{t.tip ? ` (tip ${formatCurrency(t.tip)})` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </SheetContent>
  </Sheet>
);

export default FrontOfHousePage;
